import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Settings, ChefHat, Trash2, ArrowLeft, MessageCircle, Send, X, Loader, Pencil } from 'lucide-react';
import { buzz as buzzApi } from '../utils/api';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';

const BuzzPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const recipeFilter = searchParams.get('recipe');
  
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredRecipeTitle, setFilteredRecipeTitle] = useState(null);
  const [expandedComments, setExpandedComments] = useState({}); // buzzId -> boolean
  const [commentText, setCommentText] = useState({}); // buzzId -> string
  const [submittingComment, setSubmittingComment] = useState({}); // buzzId -> boolean
  
  // Edit modal state
  const [editingPost, setEditingPost] = useState(null); // { buzz_id, caption }
  const [editCaption, setEditCaption] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  // Store pending scroll position from navigation state OR sessionStorage
  const pendingScrollRef = useRef(
    location.state?.scrollY ?? 
    (sessionStorage.getItem('buzzScrollY') ? parseInt(sessionStorage.getItem('buzzScrollY'), 10) : undefined)
  );

  useEffect(() => {
    fetchPosts();
  }, [recipeFilter]);

  // Restore scroll position after data loads
  useEffect(() => {
    if (!loading && pendingScrollRef.current !== undefined) {
      // Small delay to ensure DOM is ready
      requestAnimationFrame(() => {
        window.scrollTo(0, pendingScrollRef.current);
        pendingScrollRef.current = undefined;
        sessionStorage.removeItem('buzzScrollY');
      });
    }
  }, [loading]);

  // Helper to navigate to recipe with scroll position saved
  const navigateToRecipe = (recipeId) => {
    sessionStorage.setItem('buzzScrollY', window.scrollY.toString());
    navigate(`/recipe/${recipeId}?fromBuzz=true`);
  };

  const fetchPosts = async () => {
    try {
      const response = await buzzApi.getAll();
      let allPosts = response.data.posts || [];
      
      // Filter by recipe if specified
      if (recipeFilter) {
        allPosts = allPosts.filter(p => p.recipe_id === recipeFilter);
        // Get recipe title from first post
        if (allPosts.length > 0 && allPosts[0].recipe) {
          setFilteredRecipeTitle(allPosts[0].recipe.title);
        }
      }
      
      setPosts(allPosts);
    } catch (error) {
      console.error('Failed to fetch buzz posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePost = async (buzzId, e) => {
    e.stopPropagation();
    
    try {
      await buzzApi.delete(buzzId);
      setPosts(prev => prev.filter(p => p.buzz_id !== buzzId));
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post. Please try again.');
    }
  };

  const toggleComments = (buzzId) => {
    setExpandedComments(prev => ({
      ...prev,
      [buzzId]: !prev[buzzId]
    }));
  };

  const handleAddComment = async (buzzId) => {
    const text = commentText[buzzId]?.trim();
    if (!text) return;
    
    setSubmittingComment(prev => ({ ...prev, [buzzId]: true }));
    
    try {
      const response = await buzzApi.addComment(buzzId, text);
      const newComment = response.data.comment;
      
      // Update posts with new comment
      setPosts(prev => prev.map(post => {
        if (post.buzz_id === buzzId) {
          return {
            ...post,
            comments: [...(post.comments || []), newComment]
          };
        }
        return post;
      }));
      
      // Clear input
      setCommentText(prev => ({ ...prev, [buzzId]: '' }));
    } catch (error) {
      console.error('Failed to add comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(prev => ({ ...prev, [buzzId]: false }));
    }
  };

  const handleDeleteComment = async (buzzId, commentId) => {
    try {
      await buzzApi.deleteComment(buzzId, commentId);
      
      // Update posts to remove comment
      setPosts(prev => prev.map(post => {
        if (post.buzz_id === buzzId) {
          return {
            ...post,
            comments: (post.comments || []).filter(c => c.comment_id !== commentId)
          };
        }
        return post;
      }));
    } catch (error) {
      console.error('Failed to delete comment:', error);
      alert('Failed to delete comment.');
    }
  };

  // Edit buzz post handlers
  const handleEditPost = (post, e) => {
    e.stopPropagation();
    setEditingPost(post);
    setEditCaption(post.caption || '');
  };

  const handleSaveEdit = async () => {
    if (!editingPost) return;
    
    setSavingEdit(true);
    try {
      await buzzApi.update(editingPost.buzz_id, { caption: editCaption.trim() });
      
      // Update post in local state
      setPosts(prev => prev.map(post => {
        if (post.buzz_id === editingPost.buzz_id) {
          return { ...post, caption: editCaption.trim() };
        }
        return post;
      }));
      
      setEditingPost(null);
      setEditCaption('');
    } catch (error) {
      console.error('Failed to update post:', error);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
    setEditCaption('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    
    if (diffMins < 1) {
      return 'now';
    } else if (diffMins < 60) {
      return `${diffMins}m`;
    } else if (diffHours < 24) {
      return `${diffHours}h`;
    } else if (diffDays < 7) {
      return `${diffDays}d`;
    } else if (diffWeeks < 4) {
      return `${diffWeeks}w`;
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Media cache for lazy loading
  const [mediaCache, setMediaCache] = useState({}); // buzzId -> media array
  const [loadingMedia, setLoadingMedia] = useState({}); // buzzId -> boolean

  // Lazy load media for a post
  const loadMediaForPost = async (buzzId) => {
    if (mediaCache[buzzId] || loadingMedia[buzzId]) return;
    
    setLoadingMedia(prev => ({ ...prev, [buzzId]: true }));
    try {
      const response = await fetch(`${window.location.origin}/api/buzz/${buzzId}/media`);
      if (response.ok) {
        const data = await response.json();
        if (data.media && data.media.length > 0) {
          setMediaCache(prev => ({ ...prev, [buzzId]: data.media }));
        }
      }
    } catch (error) {
      console.error('Failed to load media for buzz:', buzzId, error);
    } finally {
      setLoadingMedia(prev => ({ ...prev, [buzzId]: false }));
    }
  };

  // Load media for posts that have it
  useEffect(() => {
    posts.forEach(post => {
      if ((post.has_media || post.media_count > 0) && !mediaCache[post.buzz_id] && !loadingMedia[post.buzz_id]) {
        loadMediaForPost(post.buzz_id);
      }
    });
  }, [posts]);

  // Helper: render media (now uses mediaCache)
  const renderMedia = (post) => {
    const media = mediaCache[post.buzz_id] || post.media;
    const isLoading = loadingMedia[post.buzz_id];
    const hasMedia = post.has_media || post.media_count > 0;
    
    if (!hasMedia && (!media || media.length === 0)) return null;
    
    if (isLoading || (!media && hasMedia)) {
      return (
        <div className="relative w-40 h-40 flex-shrink-0 bg-gray-100 flex items-center justify-center">
          <Loader className="animate-spin text-gray-400" size={24} />
        </div>
      );
    }
    
    if (!media || media.length === 0) return null;
    
    const item = media[0];
    const isBase64 = item.data && !item.data.startsWith('http');
    const src = isBase64 
      ? `data:${item.type === 'video' ? 'video/mp4' : 'image/jpeg'};base64,${item.data}`
      : item.data;
    
    return (
      <div className="relative w-40 h-40 flex-shrink-0">
        {item.type === 'video' ? (
          <video src={src} className="w-full h-full object-cover" />
        ) : (
          <img src={src} alt="Buzz" className="w-full h-full object-cover" />
        )}
        {media.length > 1 && (
          <div className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
            +{media.length - 1}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40 border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🐝</span>
            <h1 className="text-2xl font-bold text-slate-800">My Buzz</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
        {/* Filter indicator when viewing recipe-specific buzz */}
        {recipeFilter && filteredRecipeTitle && (
          <div className="mb-4 bg-gray-50 rounded-lg p-3 border border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(`/recipe/${recipeFilter}?fromBuzz=true`)}
                className="text-sm text-gray-600 hover:text-amber-600 flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Back to Recipe
              </button>
              <button
                onClick={() => navigate('/buzz')}
                className="text-sm text-amber-600 hover:text-amber-700"
              >
                All Buzz →
              </button>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <ChefHat size={16} className="text-amber-600" />
              <span className="text-sm text-slate-700">
                Showing buzz for: <button 
                  onClick={() => navigate(`/recipe/${recipeFilter}?fromBuzz=true`)}
                  className="font-semibold hover:text-amber-600 hover:underline"
                >
                  {filteredRecipeTitle}
                </button>
              </span>
            </div>
          </div>
        )}
        
        {loading ? (
          <LoadingSpinner message="Loading your buzz..." />
        ) : posts.length === 0 ? (
          <EmptyState
            icon="🍯"
            title="No buzz yet!"
            description="Share what you're cooking! Your buzz posts create a personal cooking journal."
            action={
              <button
                onClick={() => navigate('/recipe-box')}
                className="inline-flex items-center gap-2 bg-amber-600 text-white px-6 py-3 rounded-full hover:bg-amber-700 transition-colors font-medium shadow-lg"
              >
                <ChefHat size={20} />
                Go to Recipe Box
              </button>
            }
            footer={
              <p className="text-xs text-gray-400">
                Tip: After cooking a recipe, you can share it to your Buzz!
              </p>
            }
          />
        ) : (
          <div className="space-y-4">
            {posts.map((post) => {
              const comments = post.comments || [];
              const isExpanded = expandedComments[post.buzz_id];
              const currentCommentText = commentText[post.buzz_id] || '';
              const isSubmitting = submittingComment[post.buzz_id];
              
              return (
                <div 
                  key={post.buzz_id} 
                  className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
                >
                  <div className="flex">
                    {/* Media - compact square thumbnail (lazy loaded) */}
                    {renderMedia(post)}
                    
                    {/* Content */}
                    <div className="flex-1 p-3 flex flex-col min-w-0">
                      {/* Recipe link */}
                      {post.recipe && (
                        <button
                          onClick={() => navigateToRecipe(post.recipe_id)}
                          className="text-left mb-1"
                        >
                          <span className="font-medium text-slate-800 text-sm hover:text-amber-600 line-clamp-1">
                            {post.recipe.title}
                          </span>
                        </button>
                      )}
                      
                      {/* Caption */}
                      {post.caption && (
                        <p className="text-gray-600 text-sm flex-1">{post.caption}</p>
                      )}
                      
                      {/* Footer - simplified for personal cooking journal */}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                        <span className="text-xs text-gray-400">{formatDate(post.created_at)}</span>
                        {/* Edit and Delete buttons */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={(e) => handleEditPost(post, e)}
                            className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                            title="Edit"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={(e) => handleDeletePost(post.buzz_id, e)}
                            className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Comments Section removed for MVP personal cooking journal */}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {editingPost && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-semibold text-slate-800">Edit Buzz</h3>
              <button
                onClick={handleCancelEdit}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-4">
              <textarea
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                placeholder="What did you think of this recipe?"
                className="w-full h-32 p-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={savingEdit}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {savingEdit ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default BuzzPage;
